/**
 * Data model — Suturia (design §14).
 * 100% local persistence (localStorage); server sync stores this same
 * document as the `payload` JSON column, so FIELD NAMES ARE A WIRE CONTRACT:
 * do not rename them without a data migration.
 */

export type Sex = 'F' | 'M' | 'X';

export type Technique =
  | 'Open'
  | 'Laparoscopic'
  | 'Robotic'
  | 'Endoscopic'
  | 'Other';

export type Role =
  | 'Lead surgeon'
  | 'First assistant'
  | 'Second assistant'
  | 'Supervisor';

/** Clavien-Dindo classification */
export type ClavienDindoGrade = 'I' | 'II' | 'IIIa' | 'IIIb' | 'IVa' | 'IVb' | 'V';

/** Long-term postoperative complications tracked for follow-up. */
export type LongTermComplication =
  | 'surgicalSiteInfection'
  | 'fistula'
  | 'ileus'
  | 'bleeding'
  | 'reintervention'
  | 'readmission'
  | 'chronicPain'
  | 'other';

export const LONG_TERM_COMPLICATIONS: LongTermComplication[] = [
  'surgicalSiteInfection',
  'fistula',
  'ileus',
  'bleeding',
  'reintervention',
  'readmission',
  'chronicPain',
  'other',
];

export interface Patient {
  iniciales: string; // anonymized patient code, e.g. "PT-2026-0042" (wire contract; legacy records may hold manual initials)
  edad: number; // age in years (wire contract)
  sexo: Sex; // sex (wire contract)
  /**
   * Optional patient ID — wire key kept for sync/export compat.
   * Prefer `pid:v2:<lookup>.<aes-gcm>` (reversible with account DEK).
   * Legacy `pid:v1:<sha256>` hashes remain until the surgeon re-enters the ID.
   * Never persist plaintext after save.
   */
  historiaClinica?: string;
  /** Last 4 chars of the raw patient ID (fallback UI for legacy `pid:v1:`). */
  historiaClinicaHint?: string;
}

export interface Complications {
  hubo: boolean; // whether a complication occurred (wire contract)
  descripcion?: string; // free-text description (wire contract)
  grado?: ClavienDindoGrade; // Clavien-Dindo grade (wire contract)
}

/** Dated follow-up event — one complication (or note) in the postoperative story. */
export interface SeguimientoEvento {
  id: string;
  /** YYYY-MM-DD */
  fecha: string;
  tipo: LongTermComplication;
  nota: string;
}

/** Follow-up data attached to a surgery (postoperative course + pathology). */
export interface Seguimiento {
  /** Legacy chip list — still written from `eventos` for older clients. */
  complicacionesLargoPlazo: LongTermComplication[];
  /** Legacy free-text note — still mirrored from the newest evento when present. */
  complicacionesLargoPlazoNota?: string;
  /** Chronological postoperative events (complications with date + note). */
  eventos?: SeguimientoEvento[];
  diasHospitalizacion?: number;
  anatomiaPatologica?: string;
  notasSeguimiento?: string;
  /** Planned follow-up / review date (YYYY-MM-DD). Cleared when completed. */
  proximaRevision?: string;
}

/** Structured summary generated by the local heuristic engine (§15) */
export interface Ficha {
  resumen: string; // headline summary (wire contract)
  pasosClave: string[]; // key steps (wire contract)
  tecnicaDetalle: string; // technique detail (wire contract)
  puntosCriticos: string[]; // critical points (wire contract)
  complicacionesNota: string; // complications note (wire contract)
  lecciones: string[]; // lessons learned (wire contract)
  seguimientoNota?: string; // follow-up summary (wire contract)
  hallazgosNota?: string; // operative findings summary (wire contract)
  lang?: 'en' | 'es'; // UI language the summary was generated in (wire contract)
  generadaEn: string; // generated at, ISO 8601 (wire contract)
}

export interface Surgery {
  id: string; // uuid/nanoid
  folio: string; // "SUT-2025-0042" (auto, sequential per year; legacy "CIR-" kept)
  fecha: string; // ISO 8601 (wire contract)
  paciente: Patient; // anonymized (wire contract)
  diagnostico: string; // diagnosis (wire contract)
  categoria: string; // e.g. "Laparoscopic cholecystectomy" (wire contract)
  snomedCt?: { conceptId: string; term: string }; // optional SNOMED CT binding (wire contract)
  tecnica: Technique; // (wire contract)
  rol: Role; // (wire contract)
  duracionMin: number; // duration in minutes (wire contract)
  descripcion: string; // surgeon's free-text operative note (wire contract)
  pasos?: string[]; // technique, step by step; ordered (wire contract)
  hallazgos?: string; // operative findings — what was seen (wire contract)
  materiales: string[]; // tags: "10 mm trocar", "3-0 suture"… (wire contract)
  complicaciones: Complications; // (wire contract)
  lecciones: string; // free-text lessons (wire contract)
  seguimiento?: Seguimiento; // postoperative follow-up (wire contract)
  ficha?: Ficha; // generated on demand (wire contract)
  creadoEn: string; // created at, ISO 8601 (wire contract)
  actualizadoEn: string; // updated at, ISO 8601 (wire contract)
  demo?: boolean; // client-only: ephemeral sample data (seed.ts) — never synced to the server
  /**
   * How the record entered the archive. Optional wire field — older clients
   * ignore it. `import` = career CSV / bulk seed (may lack pasos until enriched).
   */
  origen?: 'import';
}

/** Career stage in the surgeon's trajectory (wire contract values). */
export type CareerStage = 'student' | 'resident' | 'attending';

export const CAREER_STAGES: CareerStage[] = ['student', 'resident', 'attending'];

export const CAREER_STAGE_LABELS: Record<CareerStage, string> = {
  student: 'Student',
  resident: 'Resident',
  attending: 'Attending',
};

/** One stage of the career history: "resident since 2022" (wire contract). */
export interface CareerStageEntry {
  etapa: CareerStage; // stage (wire contract)
  desdeAnio: number; // starting year (wire contract)
}

/** Rich career timeline entry for the professional portfolio (wire contract). */
export type CareerEntryKind = 'residency' | 'fellowship' | 'attending';

export const CAREER_ENTRY_KINDS: CareerEntryKind[] = [
  'residency',
  'fellowship',
  'attending',
];

/** Map legacy kinds (education / job) stored before the attending rename. */
export function normalizeCareerEntryKind(raw: unknown): CareerEntryKind {
  if (raw === 'fellowship') return 'fellowship';
  if (raw === 'attending' || raw === 'job') return 'attending';
  // 'education', 'residency', unknown → residency
  return 'residency';
}

export interface CareerEntry {
  id: string;
  kind: CareerEntryKind;
  institution: string;
  title?: string; // degree, program name, or role title
  specialty?: string;
  fromYear: number;
  toYear?: number | null; // null / omitted = current
  current?: boolean;
}

export interface Profile {
  nombre: string; // display name (wire contract)
  especialidad: string; // specialty (wire contract)
  centro: string; // hospital / institution (wire contract)
  pais?: string; // country (wire contract)
  region?: string; // state / province (wire contract)
  ciudad?: string; // city (wire contract)
  subespecialidad?: string; // subspecialty (wire contract)
  anioNacimiento?: number; // birth year (wire contract)
  anioInicio?: number; // career start year in surgery (wire contract)
  etapas?: CareerStageEntry[]; // career stage history, chronological (wire contract)
  trayectoria?: CareerEntry[]; // residency / fellowship / attending (wire contract)
  bio?: string; // public directory bio (wire contract)
  /** When true, name/specialty/hospital appear in community authorship. */
  publico?: boolean;
}

export type LibraryView = 'list' | 'grid';
export type YearFilter = number | 'all';
export type VolumeMode = 'bars' | 'line';

export interface Prefs {
  ultimoAnio: YearFilter; // last selected year filter (wire contract)
  vistaBiblioteca: LibraryView; // library view (wire contract)
  semillaCargada: boolean; // whether the seed data was loaded (wire contract)
  volumenModo?: VolumeMode; // volume chart mode (wire contract)
  /**
   * @deprecated Ignored — UI language follows the browser only.
   * Kept optional so older localStorage / export files still parse.
   */
  idioma?: 'en' | 'es';
  /** Tour version last completed (mirrored locally; server is source of truth). */
  tourSeenVersion?: string;
}

/** Export/import file format (v1) */
export interface ExportFile {
  app: 'suturia';
  version: 1;
  exportadoEn: string; // exported at, ISO 8601 (file-format contract)
  perfil: Profile; // (file-format contract)
  cirugias: Surgery[]; // (file-format contract, kept for v1 compatibility)
}

export const TECHNIQUES: Technique[] = [
  'Open',
  'Laparoscopic',
  'Robotic',
  'Endoscopic',
  'Other',
];

export const ROLES: Role[] = [
  'Lead surgeon',
  'First assistant',
  'Second assistant',
  'Supervisor',
];

export const CLAVIEN_DINDO_GRADES: ClavienDindoGrade[] = [
  'I',
  'II',
  'IIIa',
  'IIIb',
  'IVa',
  'IVb',
  'V',
];

export const EMPTY_PROFILE: Profile = { nombre: '', especialidad: '', centro: '' };

export const INITIAL_PREFS: Prefs = {
  // Default to the current year (resolved to the latest year with data by the
  // dashboard) so the year-over-year comparison is visible out of the box.
  // An explicit "All" choice by the user is still persisted and honored.
  ultimoAnio: new Date().getFullYear(),
  vistaBiblioteca: 'list',
  semillaCargada: false,
  volumenModo: 'bars',
};

/** Chart series in palette order (§3) */
export const CHART_SERIES = [
  '#3D6B63',
  '#46586B',
  '#8C9A8E',
  '#C2B28F',
  '#A35B4B',
  '#6E8B70',
] as const;

/* ─────────────────── legacy (pre-Suturia) stored values ─────────────────── */

/**
 * Spanish enum values stored by "Bibliología Quirúrgica" (bq:* keys, export
 * files v1 and server payloads written before the rebrand). Used by the
 * one-time storage migration and when normalizing server payloads so old
 * records keep working. Never shown in the UI.
 */
export const LEGACY_TECHNIQUE_VALUES: Record<string, Technique> = {
  Abierta: 'Open',
  Laparoscópica: 'Laparoscopic',
  Robótica: 'Robotic',
  Endoscópica: 'Endoscopic',
  Otra: 'Other',
};

export const LEGACY_ROLE_VALUES: Record<string, Role> = {
  'Cirujano principal': 'Lead surgeon',
  'Primer ayudante': 'First assistant',
  'Segundo ayudante': 'Second assistant',
  Supervisor: 'Supervisor',
};

/** Accepts current and legacy stored values; falls back to 'Other'. */
export function normalizeTechnique(value: unknown): Technique {
  if (typeof value !== 'string') return 'Other';
  if ((TECHNIQUES as string[]).includes(value)) return value as Technique;
  return LEGACY_TECHNIQUE_VALUES[value] ?? 'Other';
}

/** Accepts current and legacy stored values; falls back to 'Lead surgeon'. */
export function normalizeRole(value: unknown): Role {
  if (typeof value !== 'string') return 'Lead surgeon';
  if ((ROLES as string[]).includes(value)) return value as Role;
  return LEGACY_ROLE_VALUES[value] ?? 'Lead surgeon';
}

/**
 * Returns a copy of the record with enum fields normalized to the current
 * (English) values. Safe to run on already-normalized records; unknown
 * fields pass through untouched.
 */
export function normalizeSurgeryEnums<T extends { tecnica?: unknown; rol?: unknown }>(c: T): T {
  return { ...c, tecnica: normalizeTechnique(c.tecnica), rol: normalizeRole(c.rol) };
}

/** Accepts current and legacy stored pref values. */
export function normalizeLibraryView(value: unknown): LibraryView {
  if (value === 'list' || value === 'grid') return value;
  if (value === 'lista') return 'list';
  if (value === 'cuadricula') return 'grid';
  return 'list';
}

export function normalizeVolumeMode(value: unknown): VolumeMode {
  if (value === 'bars' || value === 'line') return value;
  if (value === 'barras') return 'bars';
  if (value === 'linea') return 'line';
  return 'bars';
}

export function normalizeYearFilter(value: unknown): YearFilter {
  if (value === 'all') return 'all';
  if (value === 'todos') return 'all';
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return 'all';
}
